import clsx from 'clsx';
import type { Bucket, MediaItem } from '../../types';
import { useThumbnail } from '../hooks/useImageUrl';

/**
 * A short strip of frames from a shoot.
 *
 * Outside the review screen there is otherwise nothing to look at, which makes
 * a photo tool feel like a spreadsheet. A few real frames anchor each shoot to
 * the pictures it holds.
 */
export default function ContactSheet({
  items,
  buckets = [],
  size = 'md',
  className,
}: {
  items: MediaItem[];
  buckets?: Bucket[];
  size?: 'sm' | 'md';
  className?: string;
}) {
  if (items.length === 0) {
    return (
      <div
        className={clsx(
          'checker rounded border border-ink-800',
          size === 'sm' ? 'h-11 w-full' : 'h-20 w-full',
          className,
        )}
        aria-hidden="true"
      />
    );
  }

  return (
    <div className={clsx('flex gap-1', className)}>
      {items.map((item) => (
        <Frame key={item.id} item={item} buckets={buckets} size={size} />
      ))}
    </div>
  );
}

function Frame({
  item,
  buckets,
  size,
}: {
  item: MediaItem;
  buckets: Bucket[];
  size: 'sm' | 'md';
}) {
  const url = useThumbnail(item);
  const bucket = buckets.find((b) => b.id === item.bucketId);

  return (
    <div
      className={clsx(
        'relative shrink-0 overflow-hidden rounded-[3px] bg-ink-850',
        size === 'sm' ? 'h-11 w-11' : 'h-20 w-20',
      )}
    >
      {url ? (
        <img
          src={url}
          alt=""
          className="h-full w-full object-cover"
          style={item.rotation ? { transform: `rotate(${item.rotation}deg)` } : undefined}
        />
      ) : (
        <div className="h-full w-full animate-pulse2 bg-ink-800" />
      )}

      {bucket && (
        <span
          className={clsx(
            'absolute inset-x-0 bottom-0 h-[2px]',
            bucket.kind === 'keep' ? 'bg-keep' : 'bg-reject',
          )}
        />
      )}
    </div>
  );
}
